import { useHistory, useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import size from 'lodash/size';
import QueryString from 'qs';
import moment from 'moment';
import { repositoriesSelector } from 'state/selectors/repositories';
import { getServicesSelector } from 'state/selectors/services';
import { selectedTeamSelector } from 'state/selectors/ui-state';
import { isTeamLicenseSelector } from 'state/selectors/license';
import {
  teamsSelector,
  isSelectedTeamParentSelector
} from 'state/selectors/teams';
import { FEATURE_FLAGS_NAMES } from '@linearb/linjs-common';
import { createTeamIdFilterConfig } from 'components/GridChipFilterContainer/common-filters';
import {
  convertSearchToRequest,
  setQueryFilter,
  AUTOCOMPLETE_TYPES
} from 'components/GridChipFilterContainer/helpers';
import { useEffect } from 'react';
import useSettingsService from 'components/hooks/useSettingsService';
import { COMPONENT_TYPE } from 'components/GridChipFilterContainer/consts';
import {
  CTA_TOOLTIP_TEXTS,
  CUSTOM_COMPONENTS_PLACEHOLDERS,
  DORA_DASHBOARD_V2_EVENT_DATES_LIMIT_DAYS,
  TRACKING as TRACKING_EVENTS
} from '../../../../dashboard/doraV2/dashboard.doraV2.freeTier.consts';
import CustomFilterWithCTA from '../../../../../../../../../components/customFilter/CustomFilterWithCTA';
import useDoraFeatureFlags from 'views/root/views/home/hooks/useDoraFeatureFlags';
import * as TrackingService from 'common/services/tracking.service';
import { TRACKING } from 'common/constants/constants';
import { navigateBookDemoClick } from 'common/utils/utils';
import { DEFAULT_TIME_RANGE_KEY } from 'common/constants/metrics';
import { useDefaultDateRange } from '../../../useDefaultDateRange';

const DATE_FORMAT = 'YYYY-MM-DD';

const useFilters = () => {
  const history = useHistory();
  const location = useLocation();
  const selectedTeam = useSelector(selectedTeamSelector);
  const isSelectedTeamParent = useSelector(isSelectedTeamParentSelector);
  const currentTeams = useSelector(teamsSelector);
  const isTeamLicense = useSelector(isTeamLicenseSelector);
  const { getSettingOrDefinitionValue } = useSettingsService();
  const { from, to } = useDefaultDateRange();

  const repositories = useSelector(repositoriesSelector);
  const services = useSelector(getServicesSelector);
  const servicesTabEnabledFF = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.servicesTabEnabled
  });
  const { enableTeamSelection, isAllControbutorsTeamSelected } =
    useDoraFeatureFlags();
  const shouldShowTeamsFilter =
    (!selectedTeam || isSelectedTeamParent) && !isTeamLicense;

  const statusList = ['open', 'in-progress', 'closed'];

  const customStatesNames = {
    last_30_days: 'Last 30 days',
    last_week: 'Last week',
    current_period: 'Current period'
  };

  const navigateAndTrackBookDemo = () => {
    TrackingService.logEvent(TRACKING.EVENTS.CTA_CLICK, {
      page: TRACKING.PAGES.ACTIVITY,
      tab: TRACKING.PAGE_TABS.INCIDENTS,
      cta: TRACKING_EVENTS.CTA_TYPE.FILTERS,
      intent: TRACKING.CTA_INTENT.BOOK_A_DEMO,
      source_url: window.location.href
    });
    navigateBookDemoClick();
  };

  const filtersConfig = {
    [customStatesNames.last_30_days]: {
      searchState: {
        order_by: `in_progress_between,desc`,
        in_progress_between: `${moment()
          .subtract(30, 'days')
          .format(DATE_FORMAT)},${moment().format(DATE_FORMAT)}`
      }
    },
    [customStatesNames.last_week]: {
      searchState: {
        order_by: `in_progress_between,desc`,
        in_progress_between: `${moment()
          .subtract(7, 'days')
          .format(DATE_FORMAT)},${moment().format(DATE_FORMAT)}`
      }
    },
    [customStatesNames.current_period]: {
      searchState: {
        order_by: `in_progress_between,desc`,
        in_progress_between: `${from},${to}`
      }
    },
    order_by: {
      type: 'order_by',
      reqKey: 'order_by'
    },
    status: {
      type: 'multi-checkbox',
      reqKey: 'status',
      options: statusList
    },
    repositories: {
      type: 'multi-autocomplete',
      reqKey: 'repository_ids',
      options: repositories
    },
    services: {
      type: 'multi-autocomplete',
      reqKey: 'service_ids',
      options: services
    },
    git_ref: {
      type: 'multi-checkbox',
      reqKey: 'git_ref',
      options: []
    },
    filtered_teams: createTeamIdFilterConfig(
      'multi-tree-dropdown',
      currentTeams,
      history,
      location
    ),
    in_progress_between: {
      type: 'date',
      reqKey: 'in_progress_between',
      maxDate: () => moment(),
      minDate: () =>
        isAllControbutorsTeamSelected
          ? moment().subtract(DORA_DASHBOARD_V2_EVENT_DATES_LIMIT_DAYS, 'days')
          : null
    },
    [DEFAULT_TIME_RANGE_KEY]: {
      type: 'date',
      reqKey: DEFAULT_TIME_RANGE_KEY,
      maxDate: () => moment(),
      minDate: () =>
        isAllControbutorsTeamSelected
          ? moment().subtract(DORA_DASHBOARD_V2_EVENT_DATES_LIMIT_DAYS, 'days')
          : null
    }
  };

  const filtersGroupMap = [
    {
      type: 'predefined',
      title: 'PREDEFINED FILTERS',
      page: 'Incidents',
      itemsWeight: '500',
      defaultIsOpen: true,
      names: [customStatesNames.last_30_days, customStatesNames.last_week]
    },
    {
      type: 'checkbox',
      categories: [{ name: 'status', defaultIsOpen: false, addAvatars: false }],
      title: 'Status',
      defaultIsOpen: false
    },
    ...(shouldShowTeamsFilter
      ? [
          {
            type: !enableTeamSelection
              ? COMPONENT_TYPE.CUSTOM_FILTER
              : COMPONENT_TYPE.AUTOCOMPLETE_BY_TYPE,
            items: [
              {
                type: AUTOCOMPLETE_TYPES.TREE_TEAMS_DROPDOWN,
                name: 'filtered_teams',
                addAvatars: true,
                multiple: true
              }
            ],
            title: 'Teams',
            defaultIsOpen: true,
            CustomFilter: (
              <CustomFilterWithCTA
                type={COMPONENT_TYPE.AUTOCOMPLETE}
                ctaButtonText={CTA_TOOLTIP_TEXTS.BOOK_A_DEMO}
                ctaMessage={CTA_TOOLTIP_TEXTS.ACTIVITY_TEAMS}
                customComponentProps={{
                  placeholder: CUSTOM_COMPONENTS_PLACEHOLDERS.ALL_TEAMS
                }}
                navigateBookDemoClick={navigateAndTrackBookDemo}
              />
            )
          }
        ]
      : []),
    ...(servicesTabEnabledFF
      ? [
          {
            type: !enableTeamSelection
              ? COMPONENT_TYPE.CUSTOM_FILTER
              : COMPONENT_TYPE.AUTOCOMPLETE,
            items: [
              { name: 'services', defaultIsOpen: false, addAvatars: false }
            ],
            title: 'Services',
            defaultIsOpen: true,
            CustomFilter: (
              <CustomFilterWithCTA
                type={COMPONENT_TYPE.AUTOCOMPLETE}
                ctaButtonText={CTA_TOOLTIP_TEXTS.BOOK_A_DEMO}
                ctaMessage={CTA_TOOLTIP_TEXTS.ACTIVITY_SERVICES}
                customComponentProps={{
                  placeholder: CUSTOM_COMPONENTS_PLACEHOLDERS.ALL_SERVICES
                }}
                navigateBookDemoClick={navigateAndTrackBookDemo}
              />
            )
          }
        ]
      : []),
    {
      type: 'autocomplete',
      items: [
        { name: 'repositories', defaultIsOpen: false, addAvatars: false }
      ],
      title: 'Repositories',
      defaultIsOpen: true
    },
    {
      type: 'date',
      names: ['in_progress_between'],
      defaultIsOpen: false,
      title: 'Event Dates'
    }
  ];

  const loadingSkeleton = [
    'singleline-random',
    'singleline-random',
    'singleline-random',
    'singleline',
    'singleline-random',
    'singleline-random',
    'singleline-random',
    'singleline-random',
    'singleline',
    'singleline-random',
    'singleline'
  ];

  const getReqFilterBody = () => {
    const search = QueryString.parse(location.search, {
      ignoreQueryPrefix: true
    });
    return {
      ...convertSearchToRequest(search, filtersConfig),
      teams: [selectedTeam?.id]
    };
  };

  const chipLabelMap = {
    repositories: 'Repository',
    filtered_teams: 'Team',
    services: 'Service',
    statsues: 'Status',
    in_progress_between: 'In Progress'
  };

  const setDefaultFilter = () => {
    const search = QueryString.parse(location.search, {
      ignoreQueryPrefix: true
    });
    const defaultFilter =
      filtersConfig[customStatesNames.current_period].searchState;

    if (size(search) === 0) setQueryFilter(defaultFilter);
  };

  useEffect(() => {
    setDefaultFilter();
  }, []);

  return {
    loadingSkeleton,
    getReqFilterBody,
    filtersConfig,
    filtersGroupMap,
    chipLabelMap,
    refreshOnDependencies: [selectedTeam, currentTeams]
  };
};

export default useFilters;
